import React, { useCallback, useMemo } from 'react'

import classNames from 'clsx'
import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import { CalendarToday as DateIcon } from '@mui/icons-material'
import { Box, Typography } from '@mui/material'

import { schemaTypeMap } from '~/src/Annotations/constants'
import Icon from '~/src/Annotations/Icon'
import { schemaFormatters } from '~/src/Annotations/utils'
import { phaseShape, shape as harvestShape } from '~/src/Harvest/bundle'
import createLogger from '~/src/Lib/Logging'
import { datePropType, idAndNameShape, idPropType } from '~/src/Lib/PropTypes'
import {
  durationToMillis,
  EMPTY_OBJECT,
  noop,
  shortDurationHumanizer,
} from '~/src/Lib/Utils'
import { formatAnnotationTypeTimestamp } from '~/src/Lib/Utils/datetime'
import { shape as noteShape } from '~/src/Note/bundle'
import { shape as taskShape } from '~/src/Task/bundle'
import DataTypeName from '~/src/UI/Shared/DataTypeName'
import MentionField from '~/src/UI/Shared/DescriptionMentions'
import BaseWidget from '~/src/UI/Widgets/Base'
import PersonWidget from '~/src/UI/Widgets/Person'

import Location from './Location'

const NA = i18n`N/A`

// If you need to change styles of any of the fields, you can use `makeStyles`
// with the key names the same as `fields` and pass the `classes` to Formatter:
// const useStyles = makeStyles({
//   title: { /* override styles here */ },
//   body: { /* override styles here */ },
//   date: { /* override styles here */ },
//   location: { /* override styles here */ },
//   user: { /* override styles here */ }
// })

const displayName = 'AllEventsJournal/TableRow'
const logger = createLogger(displayName)

const mentionFieldAsTextProps = { gutterBottom: true, noWrap: true, variant: 'body2' }

const CustomTableRow = ({
  classes,
  entities,
  item,
  style,
  users,
  doUpdateHash,
  isTasksTab,
  ...passthru
}) => {
  const { schema, timestamp, uuid, icon, relatedUser } = item
  // Try to get data from entity store first then fall back to item.data
  const { [uuid]: data = EMPTY_OBJECT } = entities
  const { [schema]: type } = schemaTypeMap
  const totalTimeSpent = useMemo(() => {
    if (!data.totalTimeSpent) {
      return NA
    }
    return data.totalTimeSpent === '00:00:00' ? NA : data.totalTimeSpent
  }, [data.totalTimeSpent])
  const hasTotalTimeSpent = totalTimeSpent && totalTimeSpent !== NA
  const noDialogs = type?.extraProps?.noDialogs ?? false
  const dialogUrl = noDialogs ? null : `#/${uuid.replace('_', '/')}`
  const onClick = useCallback(
    () => (noDialogs ? noop : doUpdateHash(dialogUrl, { maintainScrollPosition: true })),
    [noDialogs, doUpdateHash, dialogUrl]
  )

  if (data === EMPTY_OBJECT) {
    return null
  }

  if (schema) {
    const { [schema]: formatters } = schemaFormatters
    const {
      formatData,
    } = formatters
    const { [item.relatedUser]: itemUser } = users

    if (formatData) {
      const {
        title,
        status,
        body,
        category,
        location,
        // date,
        // avatar,
        ...formattedData
      } = formatData({
        ...passthru,
        annotation: data,
        annotationKey: uuid,
        date: timestamp,
        user: itemUser,
        entities,
      })

      return (
        <Box
          aria-disabled={noDialogs ? 'true' : 'false'}
          className={classNames(classes.tableRow, noDialogs && classes.noDialog)}
          data-testid={`journal_item_${uuid}`}
          id={uuid}
          role="button"
          style={style}
          sx={{
            gridTemplateColumns: isTasksTab
              ? 'minmax(auto, 30%) repeat(4, 1fr)'
              : 'minmax(auto, 40%) repeat(3, 1fr)'
          }}
          tabIndex={0}
          onClick={onClick}
          onKeyDown={({ key }) => {
            if (key === ' ' || key === 'Enter') {
              onClick()
            }
          }}
        >
          <div className={classes.tableCell}>
            <BaseWidget
              Icon={(
                <Icon
                  annotation={data}
                  icon={icon}
                  schema={schema}
                />
              )}
              label={category ? (
                <Typography noWrap color="textSecondary" sx={{ fontSize: 9 }}>
                  {category.toUpperCase()}
                </Typography>
              ) : null}
              title={(
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="nowrap"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                  style={{ gap: '8px' }}
                >
                  <Typography variant="body1" color="textPrimary" noWrap>
                    <DataTypeName name={title} />
                  </Typography>
                  {status}
                </Box>
              )}
              description={body ? <MentionField asText asTextProps={mentionFieldAsTextProps} value={body.split(/\n+/)[0]} /> : null}
            />
          </div>
          {isTasksTab ? (
            <div key="duration">
              <div className={classes.tableCell}>
                {hasTotalTimeSpent ? shortDurationHumanizer(durationToMillis(totalTimeSpent)) : NA}
              </div>
            </div>
          ) : null}
          <div key="location">
            <div className={classes.tableCell}>
              <Location classes={classes} data={data} location={location} />
            </div>
          </div>
          <div key="dueDate">
            <div className={classes.tableCell}>
              <BaseWidget
                Icon={DateIcon}
                title={(
                  <Typography variant="body1" color="textPrimary" className={classes.primaryText}>
                    {formattedData.duration
                      ? formattedData.duration
                      : formatAnnotationTypeTimestamp(timestamp, schema) || i18n`No due date`}
                  </Typography>
                )}
              />
            </div>
          </div>
          <div key="user">
            <PersonWidget
              title="No related user"
              user={relatedUser}
              titleProps={{ variant: 'body1', color: 'textPrimary', noWrap: true }}
            />
          </div>
        </Box>
      )
    }
  }
  console.warn('unknown schema:', schema)
  return null
}

CustomTableRow.propTypes = {
  isTasksTab: PropTypes.bool.isRequired,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  entities: PropTypes.shape({
    ...Object.keys(schemaTypeMap).reduce((shape, key) => ({
      ...shape,
      [key]: PropTypes.shape({ id: idPropType }),
    }), {}),
    dataTypes: PropTypes.objectOf(idAndNameShape).isRequired,
    devices: PropTypes.objectOf(PropTypes.shape({
      deviceName: PropTypes.string,
      lastCommunication: datePropType,
      online: PropTypes.bool,
      serialNumber: PropTypes.string,
    })).isRequired,
    facilityCultivars: PropTypes.objectOf(idAndNameShape).isRequired,
    harvests: PropTypes.objectOf(harvestShape),
    harvestPhases: PropTypes.objectOf(phaseShape),
    models: PropTypes.objectOf(idAndNameShape).isRequired,
    notes: PropTypes.objectOf(noteShape),
    notifications: PropTypes.objectOf(PropTypes.shape({
      subject: PropTypes.string,
      message: PropTypes.string,
    })),
    pesticideApplications: PropTypes.objectOf(PropTypes.shape({
      id: idPropType,
      pesticide: idPropType,
      applicationRate: PropTypes.string,
      ts: datePropType,
    })),
    pesticides: PropTypes.objectOf(idAndNameShape).isRequired,
    phases: PropTypes.objectOf(idAndNameShape).isRequired,
    targetRanges: PropTypes.objectOf(PropTypes.shape({
      id: idPropType,
      alertMin: PropTypes.number,
      alertMax: PropTypes.number,
      targetMin: PropTypes.number,
      targetMax: PropTypes.number,
      startDate: datePropType,
      endDate: datePropType,
    })),
    tasks: PropTypes.objectOf(taskShape),
  }).isRequired,
  item: PropTypes.shape({
    data: PropTypes.shape({ id: idPropType }),
    icon: PropTypes.string,
    schema: PropTypes.string,
    uuid: PropTypes.string,
    timestamp: PropTypes.string,
    relatedUser: PropTypes.number,
  }).isRequired,
  style: PropTypes.shape({
    position: PropTypes.oneOf(['absolute']),
    top: PropTypes.number,
    height: PropTypes.number,
  }).isRequired,
  users: PropTypes.objectOf(idAndNameShape).isRequired,
  doUpdateHash: PropTypes.func.isRequired,
}
CustomTableRow.displayName = displayName

const Wrapped = props => {
  const {
    doUpdateHash,
    entities,
    myFlags,
    myPermissions,
    config,
  } = useConnect(
    'selectConfig',
    'selectEntities',
    'selectMyFlags',
    'selectMyPermissions',
    'doUpdateHash',
  )

  return (
    <CustomTableRow
      {...props}
      config={config}
      entities={entities}
      myFlags={myFlags}
      myPermissions={myPermissions}
      doUpdateHash={doUpdateHash}
    />
  )
}

const Memoized = React.memo(Wrapped)
export default Memoized
